import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Users, TrendingUp, Sparkles, CreditCard, Mail, Database, Activity,
  CheckCircle2, XCircle, AlertCircle, RefreshCw, Search, X, UserCheck, UserX,
  ShieldCheck, DollarSign, Timer, Crown, Eye, LogIn, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/prefs";
import SmsHealthCard from "@/components/SmsHealthCard";

// Guard: only mount the dashboard if backend says we're a super admin.
export default function AdminDashboard() {
  const [allowed, setAllowed] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/whoami");
        setAllowed(!!data.is_super_admin);
      } catch { setAllowed(false); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/users", { params: query ? { q: query } : {} }),
      ]);
      setMetrics(m.data);
      setUsers(u.data.users);
    } catch { /* backend already prints */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (allowed) load(); }, [allowed]);
  // Refetch the user list whenever the search query changes (debounced).
  useEffect(() => {
    if (!allowed) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/users", { params: query ? { q: query } : {} });
        setUsers(data.users);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [query, allowed]);

  if (allowed === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (allowed === false) return <Navigate to="/dashboard" replace />;
  if (!metrics) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const { users: uStats, system, revenue } = metrics;
  const proPct = uStats.total > 0 ? Math.round((uStats.by_tier.pro / uStats.total) * 100) : 0;
  const activePct = uStats.total > 0 ? Math.round((uStats.by_activity.active / uStats.total) * 100) : 0;
  const activeProPct = uStats.by_tier.pro > 0 ? Math.round((uStats.active_pro / uStats.by_tier.pro) * 100) : 0;
  const maxTrend = Math.max(1, ...uStats.week_trend.map((d) => d.count));

  const fmtMoney = (v) => (Number(v) || 0).toLocaleString(undefined, {
    style: "currency", currency: revenue?.currency || "USD", maximumFractionDigits: 2,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-8" data-testid="admin-dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Activity className="w-3 h-3" /> Super Admin</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">System dashboard</h1>
          <p className="text-muted-foreground mt-2 text-sm">Hidden. Access is restricted to your account.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="admin-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </header>

      <SmsHealthCard />

      {/* Platform Health : one-glance revenue + active pro + trials */}
      <section data-testid="platform-health-section">
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Platform health</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric
            label="Total revenue"
            value={fmtMoney(revenue?.total || 0)}
            sub={`${revenue?.total_paid_count || 0} paid checkouts : ${revenue?.currency || "USD"}`}
            icon={<DollarSign className="w-4 h-4" />}
            accent="gold"
            testId="metric-revenue-total"
          />
          <Metric
            label="Revenue this month"
            value={fmtMoney(revenue?.month_to_date || 0)}
            sub={`${revenue?.month_paid_count || 0} paid this month`}
            icon={<TrendingUp className="w-4 h-4" />}
            testId="metric-revenue-month"
          />
          <Metric
            label="Active Pro users"
            value={uStats.active_pro || 0}
            sub={`${activeProPct}% of Pro base signed in last 30d`}
            icon={<Crown className="w-4 h-4" />}
            accent="gold"
            testId="metric-active-pro"
          />
          <Metric
            label="Trials in flight"
            value={uStats.trialing || 0}
            sub={
              (uStats.past_due || 0) + (uStats.canceled || 0) > 0
                ? `${uStats.past_due || 0} past due : ${uStats.canceled || 0} canceled`
                : "No churn signals"
            }
            icon={<Timer className="w-4 h-4" />}
            testId="metric-trialing"
          />
        </div>
        {revenue?.by_plan && Object.keys(revenue.by_plan).length > 0 && (
          <div className="mt-4 glass-card rounded-2xl p-5" data-testid="revenue-by-plan">
            <div className="text-[0.55rem] uppercase tracking-[0.24em] text-muted-foreground mb-3">Revenue by plan</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(revenue.by_plan).map(([plan, agg]) => (
                <div key={plan} className="flex items-baseline justify-between border-b border-white/5 pb-2" data-testid={`plan-row-${plan}`}>
                  <div>
                    <div className="text-sm font-medium capitalize">{plan}</div>
                    <div className="text-[0.6rem] text-muted-foreground uppercase tracking-wider">{agg.count} paid</div>
                  </div>
                  <div className="font-display text-lg tabular gold">{fmtMoney(agg.total)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* User Growth */}
      <section>
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Users className="w-3 h-3" /> User growth</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Metric label="Total users" value={uStats.total} icon={<Users className="w-4 h-4" />} testId="metric-total" />
          <Metric label="New today" value={uStats.new_today} icon={<TrendingUp className="w-4 h-4" />} accent="gold" testId="metric-today" />
          <div className="glass-card rounded-2xl p-5" data-testid="metric-week-trend">
            <div className="text-xs text-muted-foreground mb-2">Last 7 days</div>
            <div className="flex items-end gap-1 h-14">
              {uStats.week_trend.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.date}: ${d.count}`}>
                  <div
                    className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors"
                    style={{ height: `${(d.count / maxTrend) * 100}%`, minHeight: d.count > 0 ? "3px" : "0" }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[0.55rem] text-muted-foreground mt-1 tabular">
              <span>{uStats.week_trend[0]?.date}</span>
              <span>{uStats.week_trend[6]?.date}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Status */}
      <section>
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Subscription status</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Metric label="Free tier" value={uStats.by_tier.free} sub={`${100 - proPct}% of users`} testId="metric-tier-free" />
          <Metric label="Pro tier" value={uStats.by_tier.pro} sub={`${proPct}% of users`} accent="gold" testId="metric-tier-pro" />
        </div>
        {uStats.by_tier.pro === 0 && (
          <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
            No paying users yet. Wire up Stripe (see System Health below) then flip a user's <code className="text-primary">subscription_tier</code> to <code className="text-primary">pro</code> on successful checkout.
          </div>
        )}
      </section>

      {/* Account Activity */}
      <section>
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Activity className="w-3 h-3" /> Account activity (last {uStats.activity_window_days} days)</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Metric label="Active" value={uStats.by_activity.active} sub={`${activePct}% signed in`} icon={<UserCheck className="w-4 h-4" />} accent="gold" testId="metric-active" />
          <Metric label="Inactive" value={uStats.by_activity.inactive} sub={`${100 - activePct}% dormant`} icon={<UserX className="w-4 h-4" />} testId="metric-inactive" />
        </div>
      </section>

      {/* User Directory. Clickable. Drawer opens with full account view. */}
      <section>
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Users className="w-3 h-3" /> User directory</div>
        <div className="glass-card rounded-2xl overflow-hidden" data-testid="admin-user-directory">
          <div className="p-3 border-b border-white/8 flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              className="border-0 bg-transparent focus-visible:ring-0 h-8"
              data-testid="admin-user-search"
            />
            <span className="text-xs text-muted-foreground shrink-0 tabular">{users.length} shown</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background/95 backdrop-blur">
                <tr className="text-left text-[0.6rem] text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Signed up</th>
                  <th className="px-4 py-2 text-right">Last active</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium truncate max-w-[220px]">{u.name || "-"}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[240px]">{u.email}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-primary text-xs"><ShieldCheck className="w-3 h-3" />admin</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">user</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className={u.subscription_tier === "pro" ? "gold font-medium" : "text-muted-foreground"}>{u.subscription_tier}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1 text-[hsl(var(--success))] text-xs"><UserCheck className="w-3 h-3" />active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><UserX className="w-3 h-3" />inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground tabular">
                      {u.created_at ? formatDate(new Date(u.created_at)) : "-"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground tabular text-right">
                      {u.last_login_at ? formatDate(new Date(u.last_login_at)) : "never"}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-muted-foreground">No users match that search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* System Health */}
      <section>
        <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Sparkles className="w-3 h-3" /> System health</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <HealthCard title="AI Insights" icon={<Sparkles className="w-4 h-4" />} status={system.ai_insights.status} detail={system.ai_insights.detail} testId="health-ai" />
          <HealthCard title="Stripe payments" icon={<CreditCard className="w-4 h-4" />} status={system.stripe.status} detail={system.stripe.detail} testId="health-stripe" />
          <HealthCard title="SendGrid email" icon={<Mail className="w-4 h-4" />} status={system.sendgrid.status} detail={system.sendgrid.sender ? `Sender: ${system.sendgrid.sender}` : "Not configured"} testId="health-sendgrid" />
          <HealthCard title="Database" icon={<Database className="w-4 h-4" />} status={system.database.status} detail={`${Object.values(system.database.counts).reduce((a, b) => a + b, 0)} records across ${Object.keys(system.database.counts).length} collections`} testId="health-db" />
        </div>
        <div className="mt-4 glass-card rounded-2xl p-4">
          <div className="label-eyebrow mb-2">Content counts</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {Object.entries(system.database.counts).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 pb-1">
                <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                <span className="tabular font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="text-[0.6rem] text-muted-foreground text-right">
        Generated {new Date(metrics.generated_at).toLocaleString()}
      </div>

      <UserDetailDialog
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
      />

      <ImpersonationLog />
    </div>
  );
}

/**
 * Full account view for a single user. Opens as a glass dialog from the
 * clickable directory row. Fetches /admin/users/{id} on mount to get the
 * detail payload (referrals, snapshots, certifications).
 */
function UserDetailDialog({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!userId) { setDetail(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${userId}`);
        setDetail(data);
      } catch { setDetail({ error: true }); }
    })();
  }, [userId]);

  const signInAs = async () => {
    if (!detail?.user) return;
    const label = detail.user.name || detail.user.email;
    if (!window.confirm(`Sign in as ${label}? You will see the app exactly as they do. Return to admin via the top banner.`)) {
      return;
    }
    setImpBusy(true);
    try {
      await api.post(`/admin/impersonate/${detail.user.id}`);
      await refresh();
      toast.success(`Signed in as ${label}`);
      onClose();
      navigate("/dashboard");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impersonation failed.");
    } finally {
      setImpBusy(false);
    }
  };

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-user-detail">
        <DialogHeader>
          <DialogTitle>Account details</DialogTitle>
        </DialogHeader>
        {!detail ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : detail.error ? (
          <div className="text-sm text-destructive">Could not load user.</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center text-primary font-semibold text-lg">
                {(detail.user.name || detail.user.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg">{detail.user.name || "-"}</div>
                <div className="text-sm text-muted-foreground truncate">{detail.user.email}</div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  {detail.user.role === "admin" && (
                    <span className="inline-flex items-center gap-1 text-primary"><ShieldCheck className="w-3 h-3" />admin</span>
                  )}
                  <span className={detail.user.subscription_tier === "pro" ? "gold font-medium" : "text-muted-foreground"}>
                    {detail.user.subscription_tier || "free"}
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">{detail.user.plan_status || "-"}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Signed up" value={detail.user.created_at ? formatDate(new Date(detail.user.created_at)) : "-"} />
              <Field label="Last login" value={detail.user.last_login_at ? formatDate(new Date(detail.user.last_login_at)) : "never"} />
              <Field label="Referral code" value={detail.user.referral_code || "-"} />
              <Field label="Referred by" value={detail.user.referred_by ? detail.user.referred_by.slice(0, 8) + "…" : "-"} />
              <Field label="Referrals brought in" value={detail.referrals} />
              <Field label="Certifications" value={detail.activity.certifications} />
              <Field label="Menu snapshots" value={detail.activity.snapshots} />
              <Field label="Trial ends" value={detail.user.trial_ends_at ? formatDate(new Date(detail.user.trial_ends_at)) : "-"} />
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw record</summary>
              <pre className="mt-2 bg-black/40 rounded p-2 overflow-x-auto text-[0.65rem]">{JSON.stringify(detail.user, null, 2)}</pre>
            </details>

            {/* Impersonate: sign in as this user for support and debugging.
                Backend rejects impersonating another admin and self. */}
            {detail.user.role !== "admin" && (
              <div className="pt-2 border-t border-white/5">
                <Button
                  onClick={signInAs}
                  disabled={impBusy}
                  data-testid="sign-in-as"
                  variant="outline"
                  className="w-full gap-2"
                >
                  {impBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  Sign in as {detail.user.name || detail.user.email}
                </Button>
                <p className="text-[0.6rem] text-muted-foreground mt-1.5 text-center">
                  You will see the app as they do. A gold banner shows across the top with `Return to admin`.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div className="border-b border-white/5 pb-1">
      <div className="text-muted-foreground text-[0.6rem] uppercase tracking-wider">{label}</div>
      <div className="tabular font-medium">{value ?? "-"}</div>
    </div>
  );
}

function Metric({ label, value, sub, icon, accent, testId }) {
  return (
    <div className="glass-card rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between text-muted-foreground text-xs">
        <span>{label}</span>
        {icon && <span className={accent === "gold" ? "gold" : ""}>{icon}</span>}
      </div>
      <div className={`font-display text-4xl tabular mt-2 ${accent === "gold" ? "gold" : ""}`}>{value}</div>
      {sub && <div className="text-[0.65rem] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function HealthCard({ title, icon, status, detail, testId }) {
  const ok = status === "ok";
  const notConfig = status === "not_configured";
  const StatusIcon = ok ? CheckCircle2 : notConfig ? AlertCircle : XCircle;
  const color = ok ? "text-[hsl(var(--success))]" : notConfig ? "text-primary/80" : "text-destructive/80";
  return (
    <div className="glass-card rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <span className="text-sm font-medium">{title}</span>
        </div>
        <StatusIcon className={`w-4 h-4 ${color}`} />
      </div>
      <div className={`text-xs mt-2 ${color}`}>{ok ? "Operational" : notConfig ? "Not configured" : "Error"}</div>
      <div className="text-[0.65rem] text-muted-foreground mt-1 leading-relaxed">{detail}</div>
    </div>
  );
}

/**
 * Impersonation Log. Shows the audit trail of every "Sign in as" session
 * (start + stop rows) so a super admin can spot suspicious support access.
 * Sorted newest first. Loads on mount, refresh-on-demand.
 */
function ImpersonationLog() {
  const [state, setState] = useState({ loading: true, events: [], summary: null, error: false });
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/impersonation-events", { params: { limit: 100 } });
      setState({ loading: false, events: data.events || [], summary: data.summary || null, error: false });
    } catch {
      setState({ loading: false, events: [], summary: null, error: true });
    }
  };

  useEffect(() => { load(); }, []);

  const rows = expanded ? state.events : state.events.slice(0, 10);

  const fmtWhen = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <section data-testid="impersonation-log-section">
      <div className="label-eyebrow gold mb-3 flex items-center gap-1"><Eye className="w-3 h-3" /> Impersonation log</div>
      <div className="glass-card rounded-2xl overflow-hidden" data-testid="impersonation-log-card">
        <div className="p-3 border-b border-white/8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {state.summary ? (
              <>
                <span data-testid="imp-log-total"><span className="tabular font-medium text-foreground">{state.summary.total}</span> total events</span>
                <span data-testid="imp-log-week"><span className="tabular font-medium text-foreground">{state.summary.starts_last_7d}</span> starts in last 7 days</span>
                <span data-testid="imp-log-targets"><span className="tabular font-medium text-foreground">{state.summary.unique_targets_last_7d}</span> distinct targets last 7d</span>
              </>
            ) : (
              <span>Loading audit trail...</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-8"
            onClick={load}
            disabled={state.loading}
            data-testid="imp-log-refresh"
          >
            {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </Button>
        </div>

        {state.error ? (
          <div className="p-8 text-sm text-destructive text-center">Could not load the impersonation log.</div>
        ) : state.loading && state.events.length === 0 ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : state.events.length === 0 ? (
          <div className="p-8 text-sm text-muted-foreground text-center" data-testid="imp-log-empty">
            No impersonation sessions on record yet. Rows will appear here the moment any admin uses "Sign in as".
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background/60">
                  <tr className="text-left text-[0.6rem] text-muted-foreground uppercase tracking-wider">
                    <th className="px-4 py-2">Event</th>
                    <th className="px-4 py-2">Admin</th>
                    <th className="px-4 py-2">Target user</th>
                    <th className="px-4 py-2 text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e, idx) => {
                    const isStart = e.event === "start";
                    const key = `${e.created_at}-${e.admin_id}-${e.target_id}-${idx}`;
                    return (
                      <tr key={key} className="border-t border-white/5" data-testid={`imp-log-row-${idx}`}>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${isStart ? "text-primary" : "text-muted-foreground"}`}>
                            {isStart ? <LogIn className="w-3.5 h-3.5" /> : <LogOut className="w-3.5 h-3.5" />}
                            {isStart ? "Signed in as" : "Returned to admin"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs text-muted-foreground truncate max-w-[220px]">{e.admin_email || e.admin_id || "-"}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs truncate max-w-[220px]">{e.target_email || e.target_id || "-"}</div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground tabular text-right">{fmtWhen(e.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {state.events.length > 10 && (
              <div className="border-t border-white/5 p-3 text-center">
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => setExpanded((v) => !v)}
                  data-testid="imp-log-toggle"
                >
                  {expanded ? "Show fewer" : `Show all ${state.events.length} events`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
